/**
 * Types and constants for the WORKFORCE domain of Jornada.
 *
 * TypeScript mirror of public serde types from the `jornada-workforce`
 * crate (Rust) that travel as JSON between the manager dashboard / employee
 * review and the backend. Shapes closely follow the REAL serde representation
 * (verified by reading the crate), and string values go AS IS to the DB
 * (PowerSync `text` columns), so they are not renamed lightly:
 *   - `EntryStatus`        → snake_case: proposed | edited | approved | rejected
 *   - `ProductivityClass`  → snake_case: productive | neutral | distracting
 *   - `LegalBasis`         → snake_case
 *   - `DataCategory`       → snake_case
 *   - `ActivityCategory`   → snake_case
 */

// ─── Review status of a proposed entry (EntryStatus) ──────────────────

export const ENTRY_STATUSES = ["proposed", "edited", "approved", "rejected"] as const;
export type EntryStatus = (typeof ENTRY_STATUSES)[number];

export function isEntryStatus(value: string | null | undefined): value is EntryStatus {
  return ENTRY_STATUSES.some((s) => s === value);
}

/** Normalizes raw DB `status`; unknown/null → 'proposed'. */
export function normalizeEntryStatus(value: string | null | undefined): EntryStatus {
  return isEntryStatus(value) ? value : "proposed";
}

/** `source` stamping an entry generated from unattended activity. */
export const UNATTENDED_SOURCE = "unattended";

// ─── Productivity class (ProductivityClass) ──────────────────────────

export const PRODUCTIVITY_CLASSES = ["productive", "neutral", "distracting"] as const;
export type ProductivityClass = (typeof PRODUCTIVITY_CLASSES)[number];

export function isProductivityClass(value: string | null | undefined): value is ProductivityClass {
  return PRODUCTIVITY_CLASSES.some((c) => c === value);
}

// ─── Activity category (ActivityCategory) ───────────────────────────

export const ACTIVITY_CATEGORIES = [
  "education",
  "productivity",
  "communication",
  "social",
  "games",
  "video",
  "web",
  "system",
  "sensitive",
  "other",
] as const;
export type ActivityCategory = (typeof ACTIVITY_CATEGORIES)[number];

export function isActivityCategory(value: string | null | undefined): value is ActivityCategory {
  return ACTIVITY_CATEGORIES.some((c) => c === value);
}

// ─── Legal basis / disclosure / consent (§2e) ─────────────────────────

export const LEGAL_BASES = [
  "parental_guardianship",
  "org_owned_device_policy",
  "employee_consent",
] as const;
export type LegalBasis = (typeof LEGAL_BASES)[number];

export function isLegalBasis(value: string | null | undefined): value is LegalBasis {
  return LEGAL_BASES.some((b) => b === value);
}

export type DataCategory = "app_usage" | "web_hosts" | "screen_time" | "window_titles" | "location";

export interface MonitoringScope {
  collected: DataCategory[];
  excluded_apps: string[];
  retention_days: number;
}

export interface Disclosure {
  basis: LegalBasis;
  scope: MonitoringScope;
  guardian_label: string;
  supervised_label: string;
}

export interface ConsentRecord {
  disclosure: Disclosure;
  acknowledged_by: string;
  acknowledged_at: string;
}

// ─── Work schedule (WorkSchedule) ──────────────────────────────────────
// INVERSE semantics to parental controls: `shifts` mark when it IS
// working hours; OUTSIDE schedule NOTHING is recorded (strong privacy §2e).

export interface TimeWindow {
  start_min: number;
  end_min: number;
}

export interface WorkSchedule {
  label: string;
  /** Bitmask of working days (bit0=Mon … bit6=Sun). */
  days: number;
  shifts: TimeWindow[];
}

// ─── Productivity / attribution (crate) ──────────────────────────────

export interface ProductivitySummary {
  productive_secs: number;
  neutral_secs: number;
  distracting_secs: number;
}

export interface ProjectRef {
  id: string;
  code: string;
  name: string;
}

/**
 * Attribution rule matcher (externally-tagged serde shape of Rust's
 * `Matcher` enum): matches by exact app, host suffix, or title substring.
 */
export type Matcher = { App: string } | { HostSuffix: string } | { WindowContains: string };

/** An attribution rule: if `matcher` matches, the span belongs to `project`. */
export interface ProjectRule {
  matcher: Matcher;
  project: ProjectRef;
}

export interface ProductivityCatalog {
  app_overrides: Record<string, ProductivityClass>;
  host_overrides: Record<string, ProductivityClass>;
  category_defaults: Partial<Record<ActivityCategory, ProductivityClass>>;
  fallback: ProductivityClass;
}

// ─── Readable labels (for transparency UI) ────────────────────────────

export const DATA_CATEGORY_LABELS: Record<DataCategory, string> = {
  app_usage: "apps usadas y su tiempo",
  web_hosts: "sitios web visitados (solo el dominio)",
  screen_time: "tiempo total de pantalla",
  window_titles: "títulos de las ventanas activas",
  location: "ubicación aproximada",
};

export const LEGAL_BASIS_NOTICES: Record<LegalBasis, string> = {
  parental_guardianship: "Este dispositivo está supervisado por tu madre/padre o tutor legal.",
  org_owned_device_policy:
    "Este es un dispositivo de la institución y su uso está supervisado según la política aceptada.",
  employee_consent:
    "La actividad de este dispositivo de trabajo se registra con tu consentimiento según la política laboral.",
};

export const PRODUCTIVITY_CLASS_LABELS: Record<ProductivityClass, string> = {
  productive: "productivo",
  neutral: "neutral",
  distracting: "distractor",
};

// ─── Parsing helpers for TEXT columns storing JSON ─────────────────────

/**
 * Reasonable default productivity catalog (mirror of the crate's
 * `ProductivityCatalog::baseline`) — sensible baseline for classifying
 * when a profile does not define its own (e.g. overview team ratio).
 */
export function baselineCatalog(): ProductivityCatalog {
  return {
    app_overrides: {},
    host_overrides: {},
    category_defaults: {
      productivity: "productive",
      education: "productive",
      communication: "neutral",
      system: "neutral",
      web: "neutral",
      other: "neutral",
      social: "distracting",
      games: "distracting",
      video: "distracting",
    },
    fallback: "neutral",
  };
}

/** Tolerant parsing of a TEXT column storing JSON (or already an object). */
export function parseJsonColumn<T>(raw: unknown, fallback: T): T {
  if (raw === null || raw === undefined || raw === "") {
    return fallback;
  }
  let value: unknown;
  if (typeof raw === "object") {
    value = raw;
  } else if (typeof raw === "string") {
    try {
      value = JSON.parse(raw);
    } catch {
      return fallback;
    }
  } else {
    return fallback;
  }
  // JSON deserialization trust boundary: a text column can hold any caller-chosen
  // shape `T`, which is not runtime-checkable generically. Parsing is tolerant
  // (null/blank/corrupt → fallback) and the concrete narrowing lives in the typed
  // wrappers below (parseDisclosure/parseConsent/…).
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  return value as T;
}

/** Parses the `disclosure` column (or null if missing/corrupt). */
export function parseDisclosure(raw: unknown): Disclosure | null {
  return parseJsonColumn<Disclosure | null>(raw, null);
}

/** Parses the `consent` column (or null). */
export function parseConsent(raw: unknown): ConsentRecord | null {
  return parseJsonColumn<ConsentRecord | null>(raw, null);
}

/** Parses the `work_schedule` column (or null). */
export function parseWorkSchedule(raw: unknown): WorkSchedule | null {
  return parseJsonColumn<WorkSchedule | null>(raw, null);
}

/** Parses the `productivity` column of an entry (summary; zeros by default). */
export function parseProductivity(raw: unknown): ProductivitySummary {
  return parseJsonColumn<ProductivitySummary>(raw, {
    productive_secs: 0,
    neutral_secs: 0,
    distracting_secs: 0,
  });
}

/** Parses the `detected_apps` column of an entry (list of strings). */
export function parseDetectedApps(raw: unknown): string[] {
  const list = parseJsonColumn<unknown[]>(raw, []);
  return Array.isArray(list) ? list.filter((x): x is string => typeof x === "string") : [];
}

// ─── Productivity summary (mirror of Rust ProductivitySummary) ────────

/** Total classified seconds. */
export function totalSecs(s: ProductivitySummary): number {
  return s.productive_secs + s.neutral_secs + s.distracting_secs;
}

/** Productivity ratio = productive / total, in `[0,1]`. No activity → 0. */
export function productivityRatio(s: ProductivitySummary): number {
  const total = totalSecs(s);
  return total === 0 ? 0 : s.productive_secs / total;
}

/** Empty sum (identity for team aggregation). */
export function emptySummary(): ProductivitySummary {
  return { productive_secs: 0, neutral_secs: 0, distracting_secs: 0 };
}

/** Merges two summaries (for team total). */
export function mergeSummaries(
  a: ProductivitySummary,
  b: ProductivitySummary,
): ProductivitySummary {
  return {
    productive_secs: a.productive_secs + b.productive_secs,
    neutral_secs: a.neutral_secs + b.neutral_secs,
    distracting_secs: a.distracting_secs + b.distracting_secs,
  };
}

/** Aggregated total of a list of summaries. */
export function aggregateSummaries(list: ProductivitySummary[]): ProductivitySummary {
  return list.reduce(mergeSummaries, emptySummary());
}

/**
 * Classifies activity category seconds totals into a
 * `ProductivitySummary`, applying the profile's productivity catalog
 * (default per category → fallback). Client equivalent of
 * `ProductivityCatalog::classify` scoped to category (the dashboard only has
 * `usage_event` category, never content).
 */
export function classifyCategoryTotals(
  totals: { category: string | null; total_secs: number }[],
  catalog: ProductivityCatalog | null,
): ProductivitySummary {
  const fallback: ProductivityClass = catalog?.fallback ?? "neutral";
  const summary = emptySummary();
  for (const { category, total_secs } of totals) {
    const cls =
      (isActivityCategory(category) ? catalog?.category_defaults?.[category] : undefined) ??
      fallback;
    if (cls === "productive") {
      summary.productive_secs += total_secs;
    } else if (cls === "distracting") {
      summary.distracting_secs += total_secs;
    } else {
      summary.neutral_secs += total_secs;
    }
  }
  return summary;
}

// ─── Entry review machine (anti-self-billing invariant) ────────────────
//
// HARD INVARIANT (§2e + product): NEVER self-bill. An entry is
// billable ONLY if the employee approved it AND has an assigned project. Every
// edit (project/duration/description) reverts entry to 'edited' (requires
// re-approval). Approving without a project does NOT transition to 'approved'.

/** An entry is billable only if approved AND has a project. */
export function isBillable(status: string | null | undefined, hasProject: boolean): boolean {
  return normalizeEntryStatus(status) === "approved" && hasProject;
}

/** Any employee edit reverts the entry to 'edited'. */
export function statusAfterEdit(): EntryStatus {
  return "edited";
}

/**
 * State after an APPROVE attempt: only transitions to 'approved' if there is a project;
 * otherwise, retains current state (cannot bill without project).
 */
export function statusAfterApprove(
  currentStatus: string | null | undefined,
  hasProject: boolean,
): EntryStatus {
  return hasProject ? "approved" : normalizeEntryStatus(currentStatus);
}

/** Can it be approved? Only with an assigned project. */
export function canApprove(hasProject: boolean): boolean {
  return hasProject;
}

/** Rejecting always transitions to 'rejected'. */
export function statusAfterReject(): EntryStatus {
  return "rejected";
}
